import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { SearchOff } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { MapboxGeoJSONFeature } from "mapbox-gl";

import { InteractionMode, Tree, TreeEnvironmentRef, TreeItem, TreeItemIndex } from "ui/Tree";

import { InferredIcon } from "fond/map/Legend/InferredIcon";
import { getOne } from "fond/redux/comments";
import { Configuration, GroupConfig, MapLayerConfig, Store } from "fond/types";
import { LayerConfig, LayerStyle, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { NonIdealState } from "fond/widgets";

import { buildFullTreeItems, countFeatures, filterTreeItemsByFeatures, filterTreeItemsBySearch } from "./FeaturesList.helpers";

import {
  Container,
  HeaderContainer,
  HeaderTitle,
  ItemTitleContainer,
  ItemTitleContent,
  ItemTitleContentWithIcon,
  SearchField,
  TreeContainer,
} from "./FeaturesList.styles";

interface FeaturesListProps {
  features: MapboxGeoJSONFeature[];
  config: Configuration;
  layerConfigs: Array<LayerConfig | SublayerConfig>;
  onFeatureClick: (feature: mapboxgl.MapboxGeoJSONFeature) => void;
}

export type FeaturesListItemType =
  | MapLayerConfig
  | GroupConfig
  | LayerConfig
  | SublayerConfig
  | LayerStyle
  | { Type: "Feature"; feature: MapboxGeoJSONFeature };

const FeaturesList: React.FC<FeaturesListProps> = ({ config, features, layerConfigs, onFeatureClick }) => {
  const getComment = useSelector((state: Store) => getOne(state));
  const environmentRef = useRef<TreeEnvironmentRef<FeaturesListItemType>>();
  const [searchText, setSearchText] = useState("");
  const [expandedItems, setExpandedItems] = useState<TreeItemIndex[]>([]);
  const [selectedItems, setSelectedItems] = useState<TreeItemIndex[]>([]);
  const [focusedItem, setFocusedItem] = useState<TreeItemIndex>();

  /**
   * Build full tree structure from config and augment with individual features
   */
  const fullTreeItems = useMemo(() => {
    return buildFullTreeItems(config, features, layerConfigs);
  }, [config, features, layerConfigs]);

  /**
   * Filter tree items to only include nodes that have features or have children with features.
   * For example, if a layer has no features in the current map view, it will be filtered out of the tree.
   */
  const filteredTreeItems = useMemo(() => {
    return filterTreeItemsByFeatures(fullTreeItems);
  }, [fullTreeItems]);

  /**
   * Apply search filter to tree items
   */
  const searchFilteredItems = useMemo(() => {
    return filterTreeItemsBySearch(filteredTreeItems, searchText, getComment);
  }, [filteredTreeItems, searchText, getComment]);

  /**
   * Count the number of features in the filtered tree
   */
  const filteredFeaturesCount = useMemo(() => {
    return countFeatures(searchFilteredItems);
  }, [searchFilteredItems]);

  /**
   * Auto-expand all items by default
   */
  useEffect(() => {
    // Expand all items (except root) to show all features
    const allIds = Object.keys(searchFilteredItems).filter((id) => id !== "root");
    setExpandedItems(allIds);
  }, [searchFilteredItems]);

  /**
   * Get item title
   */
  const getItemTitle = useCallback(
    (item: TreeItem<FeaturesListItemType>) => {
      if (!item.data) return "";

      if (item.data.Type === "Feature") {
        const { feature } = item.data;

        // Handle comment features
        if (feature.properties?.commentID) {
          const comment = getComment(feature.properties.commentID);
          return `Comment by ${comment?.Creator?.Email || "Unknown"}`;
        }

        // Handle regular features
        const fondId = feature.properties?.FondID || feature.properties?.BiarriID;
        const layerConfig = layerConfigs.find((cfg) => cfg.Styles.includes(feature.layer.id));
        return `${layerConfig?.Label || "Feature"}${fondId ? ` (${fondId})` : ""}`;
      }

      if (item.data.Type === "STYLE") {
        return item.data.Name;
      }
      if (item.data.Type === "MapLayerConfig") {
        return "";
      }
      if ("Label" in item.data) {
        return item.data.Label;
      }
      return "";
    },
    [layerConfigs, getComment]
  );

  /**
   * Render item title with appropriate icons and actions
   */
  const renderItemTitle = useCallback(
    ({ title, item }: { title: string; item: TreeItem<FeaturesListItemType> }) => {
      const titleClassName = `rct-tree-item-title-${item.data?.Type}`;

      // Handle feature items
      if (item.data?.Type === "Feature") {
        return (
          <ItemTitleContainer className={titleClassName}>
            <ItemTitleContent>
              <Typography noWrap fontSize={12}>
                {title}
              </Typography>
            </ItemTitleContent>
          </ItemTitleContainer>
        );
      }

      // Handle config items (Groups, Layers, Sublayers, Styles)
      // Count features from the filtered tree
      const filteredItem = searchFilteredItems[item.index];
      const featureCount =
        filteredItem?.children?.filter((childId) => {
          const child = searchFilteredItems[childId];
          return child?.data?.Type === "Feature";
        }).length || 0;

      return (
        <ItemTitleContainer className={titleClassName}>
          {item.data?.Type !== "GROUP" && item.data?.Type !== "MapLayerConfig" && (
            <InferredIcon entity={item.data as LayerConfig | SublayerConfig | LayerStyle} config={config} />
          )}
          <ItemTitleContentWithIcon>
            <Typography noWrap fontSize={12} fontWeight={item.data?.Type === "GROUP" ? 700 : undefined}>
              {title}
            </Typography>
            {featureCount > 0 && <Box component="span" color="text.secondary">{` (${featureCount})`}</Box>}
          </ItemTitleContentWithIcon>
        </ItemTitleContainer>
      );
    },
    [searchFilteredItems, config]
  );

  const handleOnFilterChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchText(event.target.value);
  };

  const handleSelectItems = useCallback(
    (items: TreeItemIndex[]) => {
      setSelectedItems(items);

      // If a single feature is selected, select it on the map
      if (items.length === 1) {
        const itemId = items[0];
        const item = searchFilteredItems[itemId];
        if (item?.data?.Type === "Feature") {
          onFeatureClick(item.data.feature);
        }
      }
    },
    [searchFilteredItems, onFeatureClick]
  );

  return (
    <Container>
      <HeaderContainer>
        <HeaderTitle variant="h6">{filteredFeaturesCount} features</HeaderTitle>
        <SearchField placeholder="Search for a feature..." onChange={handleOnFilterChange} value={searchText} size="small" />
      </HeaderContainer>
      <TreeContainer className="customScrollbars">
        {!searchFilteredItems.root ? (
          <NonIdealState size="small" description="No matching results..." icon={<SearchOff />} />
        ) : (
          <Tree<FeaturesListItemType>
            id="features-list-tree"
            ref={environmentRef}
            items={searchFilteredItems}
            mode="controlled"
            getItemTitle={getItemTitle}
            getItemClass={(item: TreeItem<FeaturesListItemType>) => `rct-tree-item-button-${item.data?.Type}`}
            renderItemTitle={renderItemTitle}
            defaultInteractionMode={InteractionMode.ClickArrowToExpand}
            onExpandItem={(item) => setExpandedItems((prev) => [...prev, item.index])}
            onCollapseItem={(item) => setExpandedItems((prev) => prev.filter((id) => id !== item.index))}
            onSelectItems={handleSelectItems}
            onFocusItem={(item) => setFocusedItem(item.index)}
            viewState={{
              expandedItems,
              selectedItems,
              focusedItem,
            }}
          />
        )}
      </TreeContainer>
    </Container>
  );
};

export default FeaturesList;
